import * as Yup from 'yup';
import { checkForInvalidVariables } from '@/utils/commonHelpers';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';

const labelStyles = { fontSize: '14px', fontWeight: 'semiBold' };

const interviewerVariables = {
  org: ['org.name'],
  job: ['job.title', 'job.current_stage'],
  candidate: [
    'candidate.first_name',
    'candidate.last_name',
    'candidate.full_name',
  ],
};

const interviewerWithVariables = () => {
  const data = {
    name: 'interviewName',
    label: 'Interview name',
    type: 'variableInput',
    isRequired: true,
    placeholder: 'Add interview name here',
    maxLength: 250,
    enableCustomVariables: true,
    customVariableList: interviewerVariables,
    labelStyles,
    variableWrapperProps: {
      pr: '10px',
    },
    validation: Yup.string()
      .required('Interview name is required')
      .test(
        'check-for-invalid-variables',
        'Provide a valid variable',
        function (value) {
          const invalidVariablesFound = checkForInvalidVariables({
            variablesListArr: Object.entries(
              interviewerVariables || {}
            ).flatMap(([, values = []]) => values),
            content: value,
          });

          if (invalidVariablesFound) return false;
          else return true;
        }
      ),
  };

  return data;
};

const interviewType = {
  name: 'interview_type',
  label: 'Interview type',
  type: 'select',
  isSearchable: false,
  isRequired: false,
  options: [
    {
      label: 'One-on-one interview',
      value: 'one_on_one',
      desc: 'Add the interviewer you want or pick an interviewer pool',
    },
    {
      label: 'Panel interview',
      value: 'panel',
      desc: 'Add all the interviewers you want to attend this interview',
    },
  ],
  labelStyles: labelStyles,
  components: {
    Option: SelectWithDescriptionOption,
  },
};

const interviewerSelectionOptions = {
  isConditional: true,
  conditionConfig: { field: 'interview_type', value: 'one_on_one' },
  name: 'interviewTypeRadio',
  type: 'radio2',
  isRequired: false,
  options: [
    {
      label: 'Interviewers',
      value: 'manual_selection',
      desc: 'Manually select interviewers for this interview',
    },
    {
      label: 'Interviewer pool',
      value: 'interview_pool',
      desc: 'Select interviewers from an interviewer pool',
    },
  ],
  // containerStyles: containerStyles,
  styles: { gap: '60px' },
};

const interviewers = {
  isConditional: true,
  conditionConfig: {
    type: 'or',
    conditions: [
      {
        type: 'and',
        conditions: [
          { field: 'interview_type', value: 'one_on_one' },
          { field: 'interviewTypeRadio', value: 'manual_selection' },
        ],
      },
      { field: 'interview_type', value: 'panel' },
    ],
  },
  name: 'participant_ids',
  label: 'Add interviewers',
  type: 'autoComplete',
  isRequired: false,
  components: {
    Option: SelectWithDescriptionOption,
  },
  extraProps: {
    showAvatar: true,
    labelKey: 'label',
    descKey: 'email',
    avatarKey: 'avatar',
    isNearLabel: true,
  },
  fetchConfig: {
    method: 'get',
    endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
    processResponse: (res) =>
      res?.data?.users?.map((item) => ({
        label: `${item?.first_name} ${item?.last_name}`,
        value: item?.id,
        avatar: item?.avatar_thumb_url,
        ...item,
      })),
    httpType: 'v1',
  },
  updateEndpointOnChange: true,
  loadDefaultOptions: false,
  isMulti: true,
  useInput: true,
  width: '100%',
  labelStyles: labelStyles,
  customFormatLabel: SelectOptionWithAvatar,
  // options: usersOptions
  placeholder: 'Select interviewer',
  showNotes: true,
  noteCondition: {
    type: 'index',
    conditions: [
      {
        type: 'and',
        conditions: [
          { field: 'interview_type', value: 'one_on_one' },
          { field: 'participant_ids', type: 'greater', value: 1 },
        ],
      },
      {
        type: 'and',
        conditions: [
          { field: 'interview_type', value: 'panel' },
          { field: 'participant_ids', type: 'greater', value: 1 },
        ],
      },
    ],
  },
  noteValues: [
    'We will select one of the users from the list below based on availability',
    'All the users selected will be added as interviewers',
  ],
};

export const useInterviewDetailsConfig = () => {
  return {
    interviewerWithVariables: interviewerWithVariables(),
    interviewType,
    interviewerSelectionOptions,
    interviewers,
  };
};
